(() => {
  const instances = new WeakSet();

  function initPage(root) {
    if (instances.has(root)) return;
    const { gsap, ScrollTrigger, ScrollToPlugin, Spotlight } = window;
    const phoneView = window.matchMedia('(max-width: 767px)');
    const mouseView = window.matchMedia('(hover: hover) and (pointer: fine)');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const pointerScale = 1.04;
    const duration = (seconds) => (reducedMotion.matches ? 0 : seconds);

    function animate(target, properties, seconds = 0.2, ease = 'power2.out') {
      return gsap.to(target, { ...properties, duration: duration(seconds), ease, overwrite: 'auto' });
    }

    function on(target, type, handler, options) {
      target?.addEventListener(type, handler, options);
    }

    function perFrame(callback) {
      let frame = 0;
      return () => {
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = 0;
          callback();
        });
      };
    }

    function lockScroll() {
      const html = document.documentElement;
      let saved = { overflow: html.style.overflow, paddingRight: html.style.paddingRight };
      const scrollbar = window.innerWidth - html.clientWidth;
      if (scrollbar > 0) {
        html.style.paddingRight = `${parseFloat(getComputedStyle(html).paddingRight) + scrollbar}px`;
      }
      html.style.overflow = 'hidden';

      return () => {
        if (!saved) return;
        html.style.overflow = saved.overflow;
        html.style.paddingRight = saved.paddingRight;
        saved = null;
      };
    }

    function clickedOutsideDialog(event, dialog) {
      if (event.target !== dialog) return false;
      const rect = dialog.getBoundingClientRect();
      return event.clientX < rect.left || event.clientX > rect.right
        || event.clientY < rect.top || event.clientY > rect.bottom;
    }

    function initViewportResize() {
      const media = gsap.matchMedia();
      media.add('(max-width: 767px)', () => {
        let width = window.innerWidth;
        const orientation = window.screen.orientation;
        let angle = orientation?.angle ?? window.orientation;
        const orientationTarget = orientation || window;
        const orientationEvent = orientation ? 'change' : 'orientationchange';
        const setHeight = () => {
          const measure = document.createElement('div');
          measure.style.cssText = 'position:fixed;top:0;left:0;width:0;height:100lvh;visibility:hidden;pointer-events:none';
          root.append(measure);
          const height = Math.ceil(Math.max(measure.getBoundingClientRect().height, window.innerHeight));
          measure.remove();
          root.style.setProperty('--chapter-mobile-height', `${height}px`);
        };
        setHeight();
        const refresh = perFrame(() => {
          if (!phoneView.matches) return;
          const nextAngle = orientation?.angle ?? window.orientation;
          if (window.innerWidth === width && nextAngle === angle) return;
          width = window.innerWidth;
          angle = nextAngle;
          setHeight();
          ScrollTrigger.refresh(true);
        });

        // Safari's browser bars resize the viewport without changing the layout width.
        ScrollTrigger.config({ autoRefreshEvents: 'visibilitychange,DOMContentLoaded,load' });
        on(window, 'resize', refresh, { passive: true });
        on(orientationTarget, orientationEvent, refresh, { passive: true });
        return () => {
          window.removeEventListener('resize', refresh);
          orientationTarget.removeEventListener(orientationEvent, refresh);
          root.style.removeProperty('--chapter-mobile-height');
          ScrollTrigger.config({ autoRefreshEvents: 'visibilitychange,DOMContentLoaded,load,resize' });
        };
      });
    }

    function initHeader() {
      const section = root.querySelector('.section--header');
      const header = section?.querySelector('.header');
      const menuButton = section?.querySelector('.header__menu');
      const logo = section?.querySelector('.header__logo');
      const orderButton = section?.querySelector('.header__order');
      const menuOrderButton = section?.querySelector('.header__menu-order');
      const bars = menuButton?.querySelectorAll('span');
      if (!section || !header || !menuButton || !logo) return;

      let remSize = parseFloat(getComputedStyle(document.documentElement).fontSize) / 10;
      const rem = (pixels) => pixels * remSize;
      let lastScrollTop = Math.max(0, window.scrollY);
      let revealStart = lastScrollTop;
      const revealScrollDistance = 80;
      let restoring = null;
      let wasPhone = phoneView.matches;
      let orderHover = false;
      let menuOrderHover = false;

      const menuIsOpen = () => section.classList.contains('is-menu-open');
      const isPinned = () => section.classList.contains('is-scrolled');

      const updateOrder = (seconds = 0.2) => {
        if (!orderButton) return;
        let backgroundColor = isPinned() ? '#8ce5ff' : '#fff';
        let color = '#000';
        if (orderHover) {
          backgroundColor = isPinned() ? '#000' : '#8ce5ff';
          color = isPinned() ? '#fff' : '#000';
        }
        animate(orderButton, { backgroundColor, color }, seconds);
      };

      const updateMenuOrder = (seconds = 0.2) => {
        if (!menuOrderButton) return;
        animate(menuOrderButton, {
          backgroundColor: menuOrderHover ? '#8ce5ff' : '#000',
          color: menuOrderHover ? '#000' : '#fff',
        }, seconds);
      };

      const setPinnedVisuals = () => {
        if (phoneView.matches) {
          gsap.set(section, { clearProps: 'backgroundColor,top' });
          gsap.set(header, { paddingLeft: rem(20), paddingRight: rem(20), backgroundColor: '#fff' });
          gsap.set(logo, { left: rem(20) });
          gsap.set(menuButton, { right: rem(20) });
        } else {
          gsap.set(section, { backgroundColor: '#fff', clearProps: 'top' });
          gsap.set([header, logo, menuButton], {
            clearProps: 'paddingLeft,paddingRight,backgroundColor,left,right',
          });
        }
        updateOrder(0);
      };

      const reveal = () => {
        if (isPinned() && !section.classList.contains('is-away') && !restoring) return;
        restoring?.kill();
        restoring = null;
        if (!isPinned()) {
          section.classList.add('is-scrolled');
          gsap.set(section, { yPercent: -180 });
        }
        setPinnedVisuals();
        section.classList.remove('is-away');
        animate(section, { yPercent: 0 }, 0.5, 'power3.out');
      };

      const hide = () => {
        if (!isPinned() || menuIsOpen() || section.classList.contains('is-away')) return;
        restoring?.kill();
        restoring = null;
        setPinnedVisuals();
        section.classList.add('is-away');
        animate(section, { yPercent: -180 }, 0.5, 'power3.out');
      };

      const restoreAtTop = () => {
        if (!isPinned() || restoring || menuIsOpen() || window.scrollY !== 0) return;
        section.classList.remove('is-away');
        gsap.killTweensOf(section);
        gsap.set(section, { backgroundColor: getComputedStyle(section).backgroundColor });
        if (phoneView.matches) {
          const headerStyle = getComputedStyle(header);
          gsap.set(header, {
            paddingLeft: headerStyle.paddingLeft,
            paddingRight: headerStyle.paddingRight,
            backgroundColor: headerStyle.backgroundColor,
          });
          gsap.set(logo, { left: getComputedStyle(logo).left });
          gsap.set(menuButton, { right: getComputedStyle(menuButton).right });
        }
        if (orderButton) {
          const orderStyle = getComputedStyle(orderButton);
          gsap.set(orderButton, { backgroundColor: orderStyle.backgroundColor, color: orderStyle.color });
        }
        section.classList.remove('is-scrolled');

        const finish = () => {
          restoring = null;
          gsap.set(section, { clearProps: 'transform,backgroundColor,top' });
          gsap.set(header, { clearProps: 'paddingLeft,paddingRight,backgroundColor' });
          gsap.set(logo, { clearProps: 'left' });
          gsap.set(menuButton, { clearProps: 'right' });
          updateOrder(0);
        };

        restoring = gsap.timeline({
          defaults: { duration: duration(0.42), ease: 'power2.out' },
          onComplete: finish,
        });
        restoring.to(
          section,
          {
            yPercent: 0,
            backgroundColor: 'transparent',
          },
          0,
        );

        if (phoneView.matches) {
          restoring.to(
            header,
            {
              paddingLeft: 0,
              paddingRight: 0,
              backgroundColor: 'transparent',
            },
            0,
          );
          restoring.to(logo, { left: 0 }, 0);
          restoring.to(menuButton, { right: 0 }, 0);
        }
        if (orderButton) {
          restoring.to(
            orderButton,
            {
              backgroundColor: orderHover ? '#8ce5ff' : '#fff',
              color: '#000',
            },
            0,
          );
        }
      };

      const animateBars = (open, instant = false) => {
        if (!bars?.length) return;
        const offset = rem(3.6);
        animate(bars[0], { y: open ? 0 : -offset, rotation: open ? 45 : 0 }, instant ? 0 : 0.3);
        animate(bars[1], { y: open ? 0 : offset, rotation: open ? -45 : 0 }, instant ? 0 : 0.3);
      };

      const setMenuOpen = (open, instant = false) => {
        if (!phoneView.matches && open) return;
        if (menuIsOpen() === open) return;

        const elements = [section, logo, menuButton];
        const oldRects = elements.map((element) => element.getBoundingClientRect());
        const oldHeaderStyle = getComputedStyle(header);
        const oldPadding = [oldHeaderStyle.paddingLeft, oldHeaderStyle.paddingRight];
        const oldBackground = oldHeaderStyle.backgroundColor;

        restoring?.kill();
        restoring = null;
        gsap.killTweensOf([section, header, logo, menuButton]);
        gsap.set(section, { yPercent: 0, clearProps: 'backgroundColor,top' });
        gsap.set(header, { clearProps: 'paddingLeft,paddingRight,backgroundColor' });
        gsap.set(logo, { clearProps: 'left' });
        gsap.set(menuButton, { clearProps: 'right' });

        section.classList.toggle('is-menu-open', open);
        document.body.classList.toggle('is-menu-open', open);
        document.documentElement.classList.toggle('is-menu-open', open);
        section.classList.remove('is-away');

        const newRects = elements.map((element) => element.getBoundingClientRect());
        const newHeaderStyle = getComputedStyle(header);
        const menuDuration = duration(instant ? 0 : 0.42);
        const sectionShift = {
          x: oldRects[0].left - newRects[0].left,
          y: oldRects[0].top - newRects[0].top,
        };
        elements.forEach((element, index) => {
          const oldRect = oldRects[index];
          const newRect = newRects[index];
          let x = oldRect.left - newRect.left;
          let y = oldRect.top - newRect.top;
          if (index > 0) {
            x -= sectionShift.x;
            y -= sectionShift.y;
          }
          if (element === menuButton) {
            x += (oldRect.width - newRect.width) / 2;
            y += (oldRect.height - newRect.height) / 2;
          }
          gsap.fromTo(
            element,
            { x, y },
            {
              x: 0,
              y: 0,
              duration: menuDuration,
              ease: 'power2.out',
              overwrite: 'auto',
              onComplete: () => gsap.set(element, { clearProps: 'x,y' }),
            },
          );
        });
        gsap.fromTo(
          header,
          { paddingLeft: oldPadding[0], paddingRight: oldPadding[1], backgroundColor: oldBackground },
          {
            paddingLeft: newHeaderStyle.paddingLeft,
            paddingRight: newHeaderStyle.paddingRight,
            backgroundColor: newHeaderStyle.backgroundColor,
            duration: menuDuration,
            ease: 'power2.out',
            overwrite: 'auto',
            onComplete: () => gsap.set(header, { clearProps: 'paddingLeft,paddingRight,backgroundColor' }),
          },
        );

        animateBars(open, instant);
        if (open) updateMenuOrder(0);
        if (!open && window.scrollY === 0) restoreAtTop();
      };

      on(menuButton, 'click', () => setMenuOpen(!menuIsOpen()));
      section
        .querySelectorAll('.header__nav a, .header__menu-order')
        .forEach((link) => on(link, 'click', () => setMenuOpen(false)));
      on(document, 'keydown', (event) => {
        if (event.key === 'Escape') setMenuOpen(false);
      });

      section.querySelectorAll('.header__link').forEach((link) => {
        on(link, 'mouseenter', () =>
          animate(link, { '--underline-opacity': 1 }, 0.25),
        );
        on(link, 'mouseleave', () =>
          animate(link, { '--underline-opacity': 0 }, 0.25),
        );
      });

      if (orderButton) {
        on(orderButton, 'mouseenter', () => {
          orderHover = true;
          updateOrder();
        });
        on(orderButton, 'mouseleave', () => {
          orderHover = false;
          updateOrder();
        });
      }
      if (menuOrderButton) {
        on(menuOrderButton, 'mouseenter', () => {
          menuOrderHover = true;
          updateMenuOrder();
        });
        on(menuOrderButton, 'mouseleave', () => {
          menuOrderHover = false;
          updateMenuOrder();
        });
      }

      const updateHeader = () => {
        const currentScrollTop = Math.max(0, window.scrollY);
        if (currentScrollTop === 0) {
          revealStart = 0;
          restoreAtTop();
        } else if (!menuIsOpen()) {
          if (currentScrollTop > lastScrollTop) revealStart = currentScrollTop;
          if (
            currentScrollTop < lastScrollTop &&
            revealStart - currentScrollTop >= revealScrollDistance &&
            currentScrollTop > 80
          ) reveal();
          else if (currentScrollTop > lastScrollTop + 1) hide();
        }
        lastScrollTop = currentScrollTop;
      };

      on(window, 'scroll', perFrame(updateHeader), { passive: true });
      on(
        window,
        'resize',
        perFrame(() => {
          remSize = parseFloat(getComputedStyle(document.documentElement).fontSize) / 10;
          if (wasPhone !== phoneView.matches) {
            setMenuOpen(false, true);
            gsap.killTweensOf([section, header, logo, menuButton]);
            gsap.set([section, header, logo, menuButton], {
              clearProps: 'x,y,transform,top,left,right,paddingLeft,paddingRight,backgroundColor',
            });
            if (isPinned()) setPinnedVisuals();
            wasPhone = phoneView.matches;
          }
          animateBars(menuIsOpen(), true);
          updateHeader();
        }),
      );
      animateBars(false, true);
      updateHeader();
    }

    function initIntroVideo(playback) {
      const introSection = root.querySelector('.section--intro');
      const introImage = introSection?.querySelector('.intro__image');
      const videoSection = root.querySelector('.section--video');
      const stage = introSection?.parentElement;
      if (!introSection || !introImage || !videoSection || !stage) return;

      let videoVisible;
      const updateVisibility = () => {
        const showVideo = Number(gsap.getProperty(videoSection, 'opacity')) > 0.001;
        if (videoVisible === showVideo) return;
        videoVisible = showVideo;
        videoSection.inert = !showVideo;
        playback?.setRevealed(showVideo);
      };
      const media = gsap.matchMedia();
      media.add(
        {
          animate: '(prefers-reduced-motion: no-preference)',
          reduce: '(prefers-reduced-motion: reduce)',
        },
        (context) => {
          let revealDelay;
          let revealTween;
          if (context.conditions.reduce) {
            gsap.set(introSection, { autoAlpha: 0 });
            gsap.set(videoSection, { autoAlpha: 1 });
          } else {
            const revealDistance = 200;
            const transitionDistance = 250;
            const videoHoldDistance = 400;
            let previousScroll = window.scrollY;
            let showingVideo = false;
            let autoReveal = false;
            const cancelReveal = () => {
              revealDelay?.kill();
              revealTween?.kill();
              revealDelay = revealTween = null;
              autoReveal = false;
            };
            const transition = gsap.timeline({ paused: true, onUpdate: updateVisibility })
              .fromTo(introSection, { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.8, ease: 'power2.inOut' })
              .fromTo(videoSection, { autoAlpha: 0 }, { autoAlpha: 1, duration: 1.3, ease: 'power2.inOut' });
            const syncVideo = (trigger, refreshing = false) => {
              const scroll = trigger.scroll();
              const nextScroll = !refreshing && scroll > previousScroll;
              const scrollingBack = !refreshing && scroll < previousScroll;
              previousScroll = scroll;
              if (scroll < trigger.start + revealDistance) {
                if (autoReveal && scrollingBack) cancelReveal();
                if (!autoReveal) {
                  showingVideo = false;
                  transition.reverse();
                }
              } else if (!showingVideo && (nextScroll || trigger.progress === 1)) {
                cancelReveal();
                showingVideo = true;
                transition.play();
              }
              updateVisibility();
            };
            ScrollTrigger.create({
              trigger: stage,
              start: 'top top',
              end: `+=${revealDistance + transitionDistance + videoHoldDistance}`,
              pin: true,
              anticipatePin: 0,
              invalidateOnRefresh: true,
              onUpdate: syncVideo,
              onRefresh: (trigger) => syncVideo(trigger, true),
            });
            gsap.set(introImage, { opacity: 0 });
            const setOpacity = gsap.quickSetter(introImage, 'opacity');
            const clamp = gsap.utils.clamp(0, 1);
            let captionScroll = window.scrollY;
            const revealCaption = (trigger) => {
              const pinStart = trigger.end - revealDistance;
              const scroll = trigger.scroll();
              if (scroll < captionScroll) cancelReveal();
              const opacity = scroll < pinStart
                ? 0.15 * clamp((scroll - trigger.start) / (pinStart - trigger.start || 1))
                : 0.15 + 0.85 * clamp((scroll - pinStart) / revealDistance);
              if (!autoReveal) setOpacity(opacity);
              if (autoReveal || showingVideo || opacity < 0.7 || opacity >= 1) {
                revealDelay?.kill();
                revealDelay = null;
              } else if (scroll !== captionScroll || !revealDelay) {
                revealDelay?.kill();
                revealDelay = gsap.delayedCall(0.4, () => {
                  revealDelay = null;
                  autoReveal = true;
                  revealTween = gsap.to(introImage, {
                    opacity: 1,
                    duration: 0.4,
                    ease: 'none',
                    onComplete: () => {
                      showingVideo = true;
                      transition.play();
                    },
                  });
                });
              }
              captionScroll = scroll;
              updateVisibility();
            };
            ScrollTrigger.create({
              trigger: stage,
              start: () => `clamp(top ${stage.offsetHeight * 0.7}px)`,
              end: `top -${revealDistance}px`,
              invalidateOnRefresh: true,
              onUpdate: revealCaption,
              onRefresh: revealCaption,
            });
          }
          updateVisibility();
          return () => {
            revealDelay?.kill();
            revealTween?.kill();
            playback?.setRevealed(false);
            videoVisible = undefined;
            videoSection.inert = true;
          };
        },
      );
    }

    function initImagePointer(section, frames) {
      const groups = [];
      for (const frame of frames) {
        const images = [...frame.querySelectorAll('img')];
        if (!images.length) continue;
        gsap.set(frame, { pointerEvents: 'auto' });
        gsap.set(images, { x: 0, y: 0, scale: pointerScale, force3D: false });
        const movements = images.map((image) => ({
          image,
          x: gsap.quickTo(image, 'x', { duration: 0.7, ease: 'power3.out' }),
          y: gsap.quickTo(image, 'y', { duration: 0.7, ease: 'power3.out' }),
        }));
        groups.push({ frame, movements });
      }
      if (!groups.length) return () => {};

      let animationFrame = 0;
      let pointer;
      let entryPoint;
      let activeGroup;

      function center(group) {
        if (!group) return;
        for (const movement of group.movements) {
          movement.x(0);
          movement.y(0);
        }
      }

      function render() {
        animationFrame = 0;
        if (!pointer) return;
        const target = document.elementFromPoint(pointer.x, pointer.y);
        const nextGroup = groups.find((group) => group.frame.contains(target));
        if (activeGroup !== nextGroup) {
          center(activeGroup);
          activeGroup = nextGroup;
          entryPoint = pointer;
        }
        if (!activeGroup) return;
        const x = gsap.utils.clamp(-1, 1, ((entryPoint.x - pointer.x) / window.innerWidth) * 2);
        const y = gsap.utils.clamp(-1, 1, ((entryPoint.y - pointer.y) / window.innerHeight) * 2);
        for (const movement of activeGroup.movements) {
          movement.x(x * movement.maxX);
          movement.y(y * movement.maxY);
        }
      }

      function queue() {
        if (pointer && !animationFrame) animationFrame = requestAnimationFrame(render);
      }

      function measure() {
        for (const group of groups) {
          for (const movement of group.movements) {
            movement.maxX = Math.min(18, movement.image.offsetWidth * 0.015);
            movement.maxY = Math.min(18, movement.image.offsetHeight * 0.015);
            const x = Number(gsap.getProperty(movement.image, 'x'));
            const y = Number(gsap.getProperty(movement.image, 'y'));
            if (Math.abs(x) > movement.maxX) {
              movement.x(0, gsap.utils.clamp(-movement.maxX, movement.maxX, x));
            }
            if (Math.abs(y) > movement.maxY) {
              movement.y(0, gsap.utils.clamp(-movement.maxY, movement.maxY, y));
            }
          }
        }
        queue();
      }

      function move(event) {
        if (event.pointerType !== 'mouse') return;
        pointer = { x: event.clientX, y: event.clientY };
        queue();
      }

      function reset() {
        cancelAnimationFrame(animationFrame);
        animationFrame = 0;
        pointer = undefined;
        entryPoint = undefined;
        center(activeGroup);
        activeGroup = undefined;
      }

      measure();
      on(section, 'pointerenter', move);
      on(section, 'pointermove', move, { passive: true });
      on(section, 'pointerleave', reset);
      on(window, 'blur', reset);
      on(window, 'scroll', queue, { passive: true });
      on(window, 'resize', measure, { passive: true });
      ScrollTrigger.addEventListener('refresh', measure);
      return () => {
        cancelAnimationFrame(animationFrame);
        section.removeEventListener('pointerenter', move);
        section.removeEventListener('pointermove', move);
        section.removeEventListener('pointerleave', reset);
        window.removeEventListener('blur', reset);
        window.removeEventListener('scroll', queue);
        window.removeEventListener('resize', measure);
        ScrollTrigger.removeEventListener('refresh', measure);
      };
    }

    function watchImagePointer(section, frames) {
      let pointerContext;
      let stop;

      const reset = () => {
        stop?.();
        pointerContext?.revert();
        stop = null;
        pointerContext = null;
      };
      const update = () => {
        reset();
        if (!mouseView.matches) return;
        pointerContext = gsap.context(() => {
          stop = initImagePointer(section, frames);
        }, section);
      };

      on(mouseView, 'change', update);
      update();
      return () => {
        mouseView.removeEventListener('change', update);
        reset();
      };
    }

    function initClosingPointer() {
      const section = root.querySelector('.section--closing');
      if (!section) return;
      const media = gsap.matchMedia();
      media.add('(prefers-reduced-motion: no-preference)', () =>
        watchImagePointer(section, [section]),
      );
    }

    function initAboutMotion() {
      const section = root.querySelector('.section--about');
      const image = section?.querySelector('.about__image');
      if (!section || !image) return;
      const media = gsap.matchMedia();
      media.add(
        '(prefers-reduced-motion: no-preference)',
        () => {
          gsap.set(image, { scale: 1.18, force3D: false });
          gsap.fromTo(image, { yPercent: 8 }, {
            yPercent: -8,
            ease: 'none',
            scrollTrigger: {
              trigger: section,
              start: 'top bottom',
              end: 'bottom top',
              scrub: 0.5,
              invalidateOnRefresh: true,
            },
          });
          return () => {
            gsap.set(image, { clearProps: 'transform' });
          };
        },
      );
    }

    function initChapters() {
      const chapters = root.querySelector('.chapters');
      const dreaming = root.querySelector('.section--dreaming');
      const reality = root.querySelector('.section--reality');
      const virtuality = root.querySelector('.section--virtuality');
      if (!chapters || !dreaming || !reality || !virtuality) return;

      const chapterLinks = [...chapters.querySelectorAll('.chapter-nav__item')];
      const chapterSections = [dreaming, reality, virtuality];
      const chapterNav = chapters.querySelector('.chapter-nav');
      const chapterRail = chapters.querySelector('.chapters__rail');
      const chapterPositions = chapterSections.map((section) => {
        const isDreaming = section === dreaming;
        return {
          section,
          opening: section.querySelector(isDreaming ? '.dreaming__pin' : '.chapter-story__opening'),
          visual: section.querySelector(isDreaming ? '.dreaming__sticky' : '.chapter-story__visual'),
          copyPanel: section.querySelector(isDreaming ? '.dreaming__scene' : '.chapter-story__copy'),
          copy: section.querySelector(isDreaming ? '.dreaming__content' : '.chapter-story__copy-inner'),
          second: section === reality ? section.querySelector('.chapter-story__second') : null,
          top: 0,
          bottom: 0,
          secondTop: Infinity,
        };
      });
      const contrastLayer = (target, className) => {
        const layer = target.cloneNode(true);
        layer.classList.add(className);
        layer.style.removeProperty('transform');
        layer.inert = true;
        layer.removeAttribute('id');
        layer.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
        target.append(layer);
        return layer;
      };
      chapterPositions.forEach((chapter) => {
        if (chapter.second && chapter.copy) {
          chapter.contrast = contrastLayer(chapter.copy, 'chapter-story__copy-contrast');
        }
      });
      const navContrast = chapterNav && contrastLayer(chapterNav, 'chapter-nav--contrast');
      const navLinks = [...chapterLinks];
      if (navContrast) navLinks.push(...navContrast.querySelectorAll('.chapter-nav__item'));
      const navBounds = { top: 0, bottom: 0, height: 0, stickyTop: 0 };
      const refreshChapterColors = perFrame(() => updateChapterColors());
      let navMask = '';

      function shadeEase(progress) {
        const midpoint = 0.3;
        if (progress < midpoint) return progress * progress / midpoint;
        return 1 - (1 - progress) ** 2 / (1 - midpoint);
      }

      const media = gsap.matchMedia();
      media.add(
        {
          phone: '(max-width: 767px)',
          desktop: '(min-width: 768px)',
          reduce: '(prefers-reduced-motion: reduce)',
        },
        (context) => {
          if (context.conditions.reduce) return;
          const phone = context.conditions.phone;
          const chapterCleanups = [];
          const preloadedImages = [];
          const imageObserver = phone ? new IntersectionObserver((entries, observer) => {
            entries.forEach(({ target, isIntersecting }) => {
              if (!isIntersecting) return;
              observer.unobserve(target);
              target.querySelectorAll('img').forEach((image) => {
                if (image.loading === 'lazy') {
                  preloadedImages.push(image);
                  image.loading = 'eager';
                }
                image.decode().catch(() => {});
              });
            });
          }, { rootMargin: `${window.innerHeight}px 0px` }) : null;
          chapterCleanups.push(() => {
            imageObserver?.disconnect();
            preloadedImages.forEach((image) => { image.loading = 'lazy'; });
          });
          chapterPositions.forEach(({ section, opening, visual, copyPanel }) => {
            if (!opening || !visual || !copyPanel) return;
            chapterCleanups.push(watchImagePointer(section, section.querySelectorAll(
              '.dreaming__sticky, .dreaming__frame--2, .dreaming__frame--3, .chapter-story__visual, .chapter-story__second',
            )));
            const isDreaming = section === dreaming;
            let stage;
            let second;
            if (phone) {
              second = isDreaming
                ? section.querySelector('.dreaming__scene--copy + .dreaming__scene')
                : section.querySelector('.chapter-story__second');
              const positions = [opening, second].map((element) => ({
                element,
                parent: element.parentNode,
                next: element.nextSibling,
              }));
              stage = document.createElement('div');
              stage.className = 'chapter-mobile-stage';
              section.append(stage);
              stage.append(opening, second);
              imageObserver.observe(stage);
              chapterCleanups.push(() => {
                positions.forEach(({ element, parent, next }) => parent.insertBefore(element, next));
                stage.remove();
              });
            }
            const images = opening.querySelectorAll(isDreaming ? '.dreaming__image' : '.chapter-story__opening-image');
            const entry = gsap.timeline({
              scrollTrigger: {
                trigger: phone ? stage : section,
                start: () => phone
                  ? `center bottom+=${opening.offsetHeight * 2 / 3}px`
                  : `top ${opening.offsetHeight * 0.4}px`,
                end: () => phone ? 'center bottom' : `+=${opening.offsetHeight}`,
                scrub: phone ? 0.15 : true,
                invalidateOnRefresh: true,
              },
            });
            if (phone) {
              const masks = ['left', 'right', 'top'].map((side) => {
                const mask = document.createElement('span');
                mask.className = `chapter-mobile-mask chapter-mobile-mask--${side}`;
                opening.append(mask);
                return mask;
              });
              chapterCleanups.push(() => masks.forEach((mask) => mask.remove()));
              entry.fromTo(masks.slice(0, 2), { scaleX: 1, force3D: true },
                { scaleX: 0, duration: 1, ease: 'none' }, 0);
              entry.fromTo(masks[2], { scaleY: 1, force3D: true },
                { scaleY: 0, duration: 1, ease: 'none' }, 0);
            } else {
              entry.fromTo(
                visual,
                { scale: 1, clipPath: 'inset(12.5% 10% 0% 10%)', force3D: false },
                { scale: 1, clipPath: 'inset(0% 0% 0% 0%)', duration: 1, ease: 'none' },
                0,
              );
            }
            entry.fromTo(
              images,
              { scale: phone ? 1.45 : 1.3, force3D: phone },
              { scale: () => mouseView.matches ? pointerScale : 1, duration: 1, ease: 'none' },
              0,
            );
            const updateEntry = () => {
              const progress = entry.progress();
              entry.invalidate().progress(progress);
              const from = phone ? 1.45 : 1.3;
              const to = mouseView.matches ? pointerScale : 1;
              gsap.set(images, { scale: gsap.utils.interpolate(from, to, progress) });
            };
            on(mouseView, 'change', updateEntry);
            chapterCleanups.push(() => mouseView.removeEventListener('change', updateEntry));

            if (phone) {
              const shadeFrame = isDreaming ? opening.querySelector('.dreaming__frame--1') : opening;
              const shade = document.createElement('span');
              shade.className = 'chapter-mobile-shade';
              shadeFrame.append(shade);
              chapterCleanups.push(() => shade.remove());
              const curtain = gsap.timeline({
                scrollTrigger: {
                  trigger: stage,
                  start: 'center bottom',
                  end: () => `+=${opening.offsetHeight}`,
                  pin: opening,
                  pinSpacing: false,
                  anticipatePin: 0,
                  scrub: true,
                  invalidateOnRefresh: true,
                },
              });
              curtain.fromTo(shade, { opacity: 0 }, {
                opacity: 1,
                duration: 0.9,
                ease: shadeEase,
              }, 0.1);
              return;
            }

            const pinHeight = () => copyPanel.offsetHeight;
            const pinEnd = () => `bottom ${pinHeight()}px`;
            ScrollTrigger.create({
              trigger: section,
              start: () => `top ${Math.min(0, pinHeight() - opening.offsetHeight)}px`,
              endTrigger: section,
              end: pinEnd,
              pin: opening,
              pinSpacing: false,
              anticipatePin: 0.25,
              invalidateOnRefresh: true,
            });

            ScrollTrigger.create({
              trigger: section,
              start: 'top top',
              endTrigger: section,
              end: pinEnd,
              pin: copyPanel,
              pinSpacing: false,
              anticipatePin: 0.25,
              invalidateOnRefresh: true,
            });

            const panels = isDreaming
              ? [...section.querySelectorAll('.dreaming__scene')]
              : [opening, section.querySelector('.chapter-story__second')];
            if (isDreaming && panels[1]) {
              ScrollTrigger.create({
                trigger: panels[1],
                start: 'top top',
                endTrigger: section,
                end: pinEnd,
                pin: panels[1],
                pinSpacing: false,
                anticipatePin: 0.25,
                invalidateOnRefresh: true,
              });
            }
            const shadeStart = 0.1;
            panels.slice(1).forEach((nextPanel, index) => {
              const target = isDreaming ? section.querySelector(`.dreaming__frame--${index + 1}`) : visual;
              const property = isDreaming ? '--dreaming-shade-opacity' : '--chapter-shade-opacity';
              gsap.fromTo(target, { [property]: 0 }, {
                [property]: 1,
                ease: shadeEase,
                scrollTrigger: {
                  trigger: nextPanel,
                  start: () => `top ${pinHeight() * (1 - shadeStart)}px`,
                  end: 'top top',
                  scrub: true,
                  invalidateOnRefresh: true,
                },
              });
            });
          });
          return () => chapterCleanups.forEach((cleanup) => cleanup());
        },
      );

      const updateChapterColors = () => {
        if (phoneView.matches) return;
        const scroll = window.scrollY;
        for (const chapter of chapterPositions) {
          if (!chapter.contrast) continue;
          const secondTop = chapter.secondTop - scroll;
          let edge = secondTop <= 0 ? 0 : 100;
          if (secondTop > 0 && secondTop < chapter.copyPanel.offsetHeight) {
            const bounds = chapter.copy.getBoundingClientRect();
            if (bounds.height) {
              edge = Math.max(0, Math.min(100, ((secondTop - bounds.top) / bounds.height) * 100));
            }
          }
          const value = `${edge.toFixed(3)}%`;
          if (chapter.edge !== value) {
            chapter.edge = value;
            chapter.contrast.style.setProperty('--chapter-color-edge', value);
          }
        }
        let mask = '';
        if (navContrast && navBounds.height) {
          const navTop = Math.min(
            Math.max(navBounds.top - scroll, navBounds.stickyTop),
            navBounds.bottom - scroll - navBounds.height,
          );
          const offset = (position) =>
            `${Math.max(0, Math.min(navBounds.height, position - scroll - navTop)).toFixed(3)}px`;
          const stops = ['transparent 0px'];
          chapterPositions.slice(1).forEach((chapter) => {
            const start = offset(chapter.top);
            const end = offset(chapter.second ? chapter.secondTop : chapter.bottom);
            stops.push(`transparent ${start}`, `#000 ${start}`, `#000 ${end}`, `transparent ${end}`);
          });
          stops.push('transparent 100%');
          mask = `linear-gradient(to bottom, ${stops.join(', ')})`;
        }
        if (mask && navMask !== mask) {
          navMask = mask;
          navContrast.style.setProperty('--chapter-nav-mask', mask);
        }
      };
      const updateChapterNav = () => {
        if (phoneView.matches) return;
        const middle = window.scrollY + chapterPositions[0].copyPanel.offsetHeight / 2;
        let active = chapterPositions[0];
        for (const chapter of chapterPositions) {
          if (chapter.top <= middle) active = chapter;
        }
        if (chapters.dataset.activeChapter !== active.section.id) {
          chapters.dataset.activeChapter = active.section.id;
          navLinks.forEach((link) => {
            link.classList.toggle('is-active', link.hash === `#${active.section.id}`);
          });
        }
        refreshChapterColors();
      };
      const measureChapters = () => {
        chapterPositions.forEach((chapter) => {
          const bounds = chapter.section.getBoundingClientRect();
          chapter.top = bounds.top + window.scrollY;
          chapter.bottom = bounds.bottom + window.scrollY;
          if (chapter.second) chapter.secondTop = chapter.second.getBoundingClientRect().top + window.scrollY;
        });
        if (chapterNav && chapterRail) {
          const bounds = chapterRail.getBoundingClientRect();
          navBounds.top = bounds.top + window.scrollY;
          navBounds.bottom = bounds.bottom + window.scrollY;
          navBounds.height = chapterNav.offsetHeight;
          navBounds.stickyTop = parseFloat(getComputedStyle(chapterNav).top) || 0;
        }
        updateChapterNav();
      };

      const updateViewport = perFrame(updateChapterNav);
      media.add('(min-width: 768px)', () => {
        on(window, 'resize', updateViewport, { passive: true });
        on(window.visualViewport, 'resize', updateViewport, { passive: true });
        ScrollTrigger.create({
          trigger: chapters,
          start: () => `top ${chapterPositions[0].opening.offsetHeight}px`,
          end: 'bottom top',
          onEnter: updateChapterNav,
          onEnterBack: updateChapterNav,
          onUpdate: updateChapterNav,
          onRefresh: measureChapters,
        });
        return () => {
          window.removeEventListener('resize', updateViewport);
          window.visualViewport?.removeEventListener('resize', updateViewport);
        };
      });
    }

    function initUi() {
      const rootStyles = getComputedStyle(document.documentElement);
      const turquoise = rootStyles.getPropertyValue('--turquoise').trim();
      const black = rootStyles.getPropertyValue('--black').trim();
      const white = rootStyles.getPropertyValue('--white').trim();

      const hover = (element, enter, leave) => {
        on(element, 'pointerenter', () => {
          if (mouseView.matches) enter();
        });
        on(element, 'pointerleave', () => {
          if (mouseView.matches) leave();
        });
      };

      const scrollCue = root.querySelector('.hero__scroll');
      if (scrollCue && !reducedMotion.matches) {
        const media = gsap.matchMedia();
        media.add('(min-width: 768px)', () => {
          const bounce = gsap.to(scrollCue, {
            y: () => parseFloat(getComputedStyle(document.documentElement).fontSize),
            duration: 1,
            ease: 'sine.inOut',
            repeat: -1,
            yoyo: true,
          });
          ScrollTrigger.create({
            trigger: root.querySelector('.section--hero'),
            start: 'top bottom',
            end: 'bottom top',
            onToggle: (trigger) => bounce.paused(!trigger.isActive),
            onRefresh: (trigger) => bounce.paused(!trigger.isActive),
          });
          const refreshBounce = perFrame(() => bounce.invalidate());
          on(window, 'resize', refreshBounce);
          return () => window.removeEventListener('resize', refreshBounce);
        });
      }

      root.querySelectorAll('.button:not(.header__order):not(.header__menu-order)').forEach((button) => {
        const style = getComputedStyle(button);
        const restingBackground = style.backgroundColor;
        const restingColor = style.color;
        const closingButton = button.classList.contains('closing__order');

        hover(
          button,
          () =>
            animate(button, {
              backgroundColor: closingButton ? black : turquoise,
              color: closingButton ? white : black,
            }),
          () =>
            animate(button, {
              backgroundColor: restingBackground,
              color: restingColor,
            }),
        );
      });

      const animateDetailsIcon = (selector, iconSelector) => {
        root.querySelectorAll(selector).forEach((details) => {
          const icon = details.querySelector(iconSelector);
          if (!icon) return;

          gsap.set(icon, {
            rotation: details.open ? 45 : 0,
            transformOrigin: '50% 50%',
          });
          on(details, 'toggle', () => {
            animate(icon, { rotation: details.open ? 45 : 0 });
          });
        });
      };

      animateDetailsIcon('.product-content__faq', 'summary img');
      animateDetailsIcon('.faq__item', 'summary svg');

      root.querySelectorAll('.faq__item summary').forEach((summary) => {
        hover(
          summary,
          () => animate(summary, { color: turquoise }, 0.3),
          () => animate(summary, { color: white }, 0.3),
        );
      });

      root.querySelectorAll('.product-content__faq summary').forEach((summary) => {
        hover(
          summary,
          () => animate(summary, { opacity: 0.6 }, 0.3),
          () => animate(summary, { opacity: 1 }, 0.3),
        );
      });

      root.querySelectorAll('.product-content__quantity-button').forEach((button) => {
        const paths = button.querySelectorAll('svg path');
        hover(
          button,
          () => {
            animate(button, { backgroundColor: turquoise }, 0.3);
            animate(paths, { fill: black }, 0.3);
          },
          () => {
            animate(button, { backgroundColor: black }, 0.3);
            animate(paths, { fill: white }, 0.3);
          },
        );
      });

      root.querySelectorAll('.product-gallery__thumb').forEach((thumb) => {
        const selected = () => thumb.classList.contains('product-gallery__thumb--active');
        const idleColor = '#afb1b5';

        gsap.set(thumb, {
          opacity: selected() ? 1 : 0.5,
          borderColor: selected() ? black : idleColor,
        });

        hover(
          thumb,
          () => animate(thumb, { opacity: 1 }),
          () => animate(thumb, { opacity: selected() ? 1 : 0.5 }),
        );
      });

      const spotlightControl = (target) =>
        target.closest?.('#spotlight.white.show .spl-header > div:not(.spl-page)');
      on(document, 'pointerover', (event) => {
        if (!mouseView.matches) return;
        const control = spotlightControl(event.target);
        if (control && !control.contains(event.relatedTarget)) {
          animate(control, { backgroundColor: '#f9f9f9' });
        }
      });
      on(document, 'pointerout', (event) => {
        if (!mouseView.matches) return;
        const control = spotlightControl(event.target);
        if (control && !control.contains(event.relatedTarget)) {
          animate(control, { backgroundColor: white });
        }
      });
    }

    function animateSpotlightSlide(fromIndex, toIndex) {
      if (fromIndex === toIndex) return;
      const scene = document.querySelector('#spotlight.show .spl-scene');
      if (!scene) return;

      gsap.killTweensOf(scene);
      if (Math.abs(toIndex - fromIndex) > 1) {
        gsap.fromTo(
          scene,
          { opacity: 0 },
          {
            opacity: 1,
            duration: duration(0.35),
            ease: 'power2.out',
            overwrite: 'auto',
          },
        );
        return;
      }

      gsap.fromTo(
        scene,
        {
          x: 0,
          xPercent: (1 - fromIndex) * 100,
          opacity: 1,
        },
        {
          x: 0,
          xPercent: (1 - toIndex) * 100,
          duration: duration(0.65),
          ease: 'power3.out',
          overwrite: 'auto',
        },
      );
    }

    function initSpotlight() {
      const nativeScale = new WeakMap();
      const scaleTweens = new WeakMap();
      const paneTranslation = new WeakMap();
      let spotlight;
      let spinner;
      let spinnerGlyph;
      let spinnerRotation;
      let closing = false;
      const observer = new MutationObserver(watchNativeChanges);

      const updateSpinner = () => {
        if (!spinner) return;
        const spinning = spinner.classList.contains('spin');
        const error = spinner.classList.contains('error');

        let opacity = 0;
        if (error) opacity = 0.5;
        if (spinning) opacity = 1;
        animate(spinner, { opacity });
        gsap.set(spinnerGlyph, { opacity: spinning ? 1 : 0 });

        if (spinning && !spinnerRotation && !reducedMotion.matches) {
          spinnerRotation = gsap.to(spinnerGlyph, {
            rotation: '+=360',
            duration: 1,
            ease: 'none',
            repeat: -1,
          });
        } else if (!spinning && spinnerRotation) {
          spinnerRotation.kill();
          spinnerRotation = null;
          gsap.set(spinnerGlyph, { rotation: 0 });
        }
      };

      const animateImageScale = (image, nextScale) => {
        if (nativeScale.get(image) === nextScale) return;
        const previousScale = scaleTweens.has(image)
          ? Number(gsap.getProperty(image, 'scale'))
          : (nativeScale.get(image) ?? 1);
        nativeScale.set(image, nextScale);
        if (Math.abs(previousScale - nextScale) < 0.001) return;

        gsap.killTweensOf(image);
        const tween = gsap.fromTo(
          image,
          {
            xPercent: -50,
            yPercent: -50,
            scale: previousScale,
          },
          {
            scale: nextScale,
            duration: duration(0.65),
            ease: 'power3.out',
            overwrite: 'auto',
            onComplete: () => scaleTweens.delete(image),
          },
        );
        if (tween.isActive()) scaleTweens.set(image, tween);
      };

      const animatePaneReset = (pane, x, y) => {
        gsap.killTweensOf(pane);
        gsap.fromTo(
          pane,
          { x, y },
          {
            x: 0,
            y: 0,
            duration: duration(0.35),
            ease: 'power2.out',
            overwrite: 'auto',
          },
        );
      };

      function watchNativeChanges(mutations) {
        const changed = new Set();
        for (const mutation of mutations) {
          if (mutation.target === spinner) updateSpinner();
          else if (mutation.attributeName === 'style') changed.add(mutation.target);
        }
        for (const target of changed) {
          if (target.tagName === 'IMG' && target.parentElement?.classList.contains('spl-pane')) {
            const match = target.style.transform.match(/^translate\(-50%,\s*-50%\) scale\(([\d.]+)\)$/);
            if (match) animateImageScale(target, Number(match[1]));
          } else if (target.classList.contains('spl-pane')) {
            const match = target.style.transform.match(/^translate\((-?[\d.]+)px,\s*(-?[\d.]+)px\)$/);
            if (match) {
              paneTranslation.set(target, { x: Number(match[1]), y: Number(match[2]) });
            } else if (!target.style.transform) {
              const previous = paneTranslation.get(target);
              paneTranslation.set(target, { x: 0, y: 0 });
              if (previous && (previous.x || previous.y)) {
                animatePaneReset(target, previous.x, previous.y);
              }
            }
          }
        }
      }

      const closeWithGsap = () => {
        if (!spotlight?.classList.contains('show') || closing) return;
        closing = true;
        gsap.killTweensOf(spotlight);
        gsap.to(spotlight, {
          opacity: 0,
          duration: duration(0.18),
          ease: 'power2.in',
          onComplete: () => {
            Spotlight.close();
            closing = false;
          },
        });
      };

      const attach = () => {
        spotlight = document.querySelector('#spotlight');
        if (!spotlight || spinner) return;
        spinner = spotlight.querySelector('.spl-spinner');
        spinnerGlyph = document.createElement('span');
        spinnerGlyph.className = 'spl-spinner__glyph';
        spinner.append(spinnerGlyph);

        on(
          spotlight,
          'click',
          (event) => {
            if (!event.target.closest('.spl-close')) return;
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            closeWithGsap();
          },
          true,
        );

        on(
          window,
          'keydown',
          (event) => {
            if (event.key !== 'Escape' || !spotlight?.classList.contains('show')) return;
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            closeWithGsap();
          },
          true,
        );
      };

      const stop = () => {
        observer.disconnect();
        spinnerRotation?.kill();
        spinnerRotation = null;
        if (spotlight) gsap.killTweensOf([spotlight, ...spotlight.querySelectorAll('*')]);
      };

      const open = () => {
        attach();
        if (!spotlight) return;
        observer.observe(spinner, { attributes: true, attributeFilter: ['class'] });
        observer.observe(spotlight.querySelector('.spl-scene'), {
          subtree: true,
          attributes: true,
          attributeFilter: ['style'],
        });
        closing = false;
        gsap.killTweensOf(spotlight);
        gsap.fromTo(
          spotlight,
          { opacity: 0 },
          {
            opacity: 1,
            duration: duration(0.25),
            ease: 'power2.out',
            overwrite: 'auto',
          },
        );
        updateSpinner();
      };
      return { open, stop };
    }

    function initAnchors() {
      let scrollTween;
      const stopScroll = () => {
        scrollTween?.kill();
        scrollTween = null;
      };
      on(window, 'touchstart', stopScroll, { passive: true });
      on(window, 'pointerdown', stopScroll, { passive: true });
      on(window, 'wheel', stopScroll, { passive: true });
      on(window, 'keydown', (event) => {
        if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) {
          stopScroll();
        }
      });

      const scrollTarget = (element) => {
        if (!element.classList.contains('section--intro')) return element;
        const stage = element.parentElement;
        const reveal = ScrollTrigger.getAll().find((trigger) => trigger.trigger === stage && !trigger.pin);
        return reveal ? Math.floor(reveal.end) : stage;
      };

      const findTarget = (hash) => {
        try {
          return root.querySelector(hash);
        } catch {
          return null;
        }
      };
      root.querySelectorAll('a[href^="#"]').forEach((link) => {
        on(link, 'click', (event) => {
          if (
            event.defaultPrevented ||
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
          )
            return;
          const hash = link.getAttribute('href');
          const target = findTarget(hash);
          if (!target) return;

          event.preventDefault();
          history.pushState(null, '', hash);
          stopScroll();
          scrollTween = gsap.to(window, {
            scrollTo: { y: scrollTarget(target), autoKill: false },
            duration: reducedMotion.matches ? 0 : 0.9,
            ease: 'power2.inOut',
            overwrite: 'auto',
          });
        });
      });

      on(
        window,
        'load',
        () => {
          if (!location.hash) return;
          const target = findTarget(location.hash);
          if (!target) return;
          ScrollTrigger.refresh();
          gsap.set(window, { scrollTo: { y: scrollTarget(target), autoKill: false } });
        },
        { once: true },
      );
    }

    function initVideo() {
      const videoSection = root.querySelector('.section--video');
      const video = videoSection?.querySelector('.video__media');
      const playButton = videoSection?.querySelector('.video__play');
      const dialog = root.querySelector('[data-sheidlina-video-popup]');
      const player = dialog?.querySelector('video');
      const source = player?.querySelector('source');
      if (!video || !playButton || !dialog || !player || !source) return;

      let revealed = false;
      let inView = false;
      let backgroundActive = false;
      let restoreScroll = () => {};
      video.muted = true;

      const updateBackground = () => {
        const active = revealed && inView && !dialog.open && !document.hidden && !reducedMotion.matches;
        if (active === backgroundActive) return;
        backgroundActive = active;
        video.autoplay = active;
        if (active) video.play().catch(() => {});
        else video.pause();
      };
      const observer = new IntersectionObserver(
        ([entry]) => {
          inView = entry.isIntersecting;
          updateBackground();
        },
        { threshold: 0 },
      );
      observer.observe(videoSection);

      const updateVideoPoster = () => {
        const device = phoneView.matches ? 'mobile' : 'desktop';
        const poster = video.getAttribute(`data-poster-${device}`);
        if (poster && video.getAttribute('poster') !== poster) video.poster = poster;
        if (poster && player.getAttribute('poster') !== poster) player.poster = poster;
      };
      const close = () => {
        player.pause();
        if (dialog.open) dialog.close();
      };

      on(playButton, 'click', () => {
        if (dialog.open || !source.dataset.src) return;
        if (!source.getAttribute('src')) {
          source.src = source.dataset.src;
          player.load();
        }
        if (player.readyState) player.currentTime = 0;
        restoreScroll = lockScroll();
        dialog.showModal();
        updateBackground();
        player.play().catch(() => {});
        dialog.querySelector('[data-video-close]')?.focus({ preventScroll: true });
      });
      on(dialog.querySelector('[data-video-close]'), 'click', close);
      on(dialog, 'cancel', (event) => {
        event.preventDefault();
        close();
      });
      on(dialog, 'click', (event) => {
        if (clickedOutsideDialog(event, dialog)) close();
      });
      on(dialog, 'close', () => {
        player.pause();
        if (player.readyState) player.currentTime = 0;
        restoreScroll();
        updateBackground();
        playButton.focus({ preventScroll: true });
      });

      on(window, 'resize', perFrame(updateVideoPoster));
      on(document, 'visibilitychange', updateBackground);
      on(reducedMotion, 'change', updateBackground);
      updateVideoPoster();
      return {
        setRevealed(visible) {
          revealed = visible;
          updateBackground();
        },
      };
    }

    function initGallery(spotlightAnimations) {
      const productSection = root.querySelector('.section--product');
      if (!productSection) return;
      const quantityValue = productSection.querySelector('.product-content__quantity-value');
      const quantityButtons = productSection.querySelectorAll('[data-quantity-change]');
      const thumbnails = [...productSection.querySelectorAll('.product-gallery__thumb')];
      const gallerySlides = [...productSection.querySelectorAll('.product-gallery__open')];
      const slider = productSection.querySelector('.product-gallery__main');
      const slides = [...productSection.querySelectorAll('.product-gallery__slide')];
      let swiper;
      const black = getComputedStyle(document.documentElement).getPropertyValue('--black').trim();
      let activeProductIndex = Math.max(0,
        thumbnails.findIndex((thumb) => thumb.classList.contains('product-gallery__thumb--active')),
      );
      let spotlightIndex = 1;

      const syncProductImage = (index) => {
        slides.forEach((slide, slideIndex) => {
          slide.inert = slideIndex !== index;
        });
        if (index === activeProductIndex) return;
        const previous = activeProductIndex;
        [previous, index].forEach((thumbnailIndex) => {
          const thumbnail = thumbnails[thumbnailIndex];
          if (!thumbnail) return;
          const selected = thumbnailIndex === index;
          thumbnail.classList.toggle('product-gallery__thumb--active', selected);
          animate(thumbnail, {
            opacity: selected || thumbnail.matches(':hover') ? 1 : 0.5,
            borderColor: selected ? black : '#afb1b5',
          });
        });
        activeProductIndex = index;
        const thumbnail = thumbnails[index];
        const thumbs = thumbnail?.parentElement;
        if (thumbs && thumbs.scrollWidth > thumbs.clientWidth) {
          const left = thumbnail.offsetLeft;
          const right = left + thumbnail.offsetWidth;
          if (left < thumbs.scrollLeft) thumbs.scrollTo({ left, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
          else if (right > thumbs.scrollLeft + thumbs.clientWidth) {
            thumbs.scrollTo({ left: right - thumbs.clientWidth, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
          }
        }
      };
      const setProductImage = (index) => {
        if (!gallerySlides[index]) return;
        swiper?.slideTo(index, duration(0.45) * 1000);
      };

      if (slider && slides.length && typeof window.Swiper === 'function') {
        swiper = new window.Swiper(slider, {
          slidesPerView: 1,
          initialSlide: activeProductIndex,
          speed: duration(0.45) * 1000,
          grabCursor: true,
          rewind: true,
          watchOverflow: true,
          focusableElements: 'input, select, textarea, video',
          on: {
            init: (instance) => syncProductImage(instance.activeIndex),
            slideChange: (instance) => syncProductImage(instance.activeIndex),
          },
        });
        on(slider, 'keydown', (event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
          event.preventDefault();
          if (event.key === 'ArrowLeft') swiper.slidePrev(duration(0.45) * 1000);
          else swiper.slideNext(duration(0.45) * 1000);
        });
      }

      const openProductImage = () => {
        const returnFocusTo = document.activeElement;
        const pageWasInert = root.inert;
        const useMobileImages = phoneView.matches;
        spotlightIndex = activeProductIndex + 1;

        Spotlight.show(
          gallerySlides.map((slide) => ({
            src: useMobileImages ? slide.dataset.spotlightMobile : slide.dataset.spotlightDesktop,
          })),
          {
            index: activeProductIndex + 1,
            theme: 'white',
            control: 'fullscreen,zoom,close,prev,next',
            page: false,
            title: false,
            description: false,
            autohide: false,
            animation: [],
            fit: 'contain',
            infinite: true,
            onchange: (index) => {
              setProductImage(index - 1);
              animateSpotlightSlide(spotlightIndex, index);
              spotlightIndex = index;
            },
            onclose: () => {
              spotlightAnimations.stop();
              root.inert = pageWasInert;
              const target = returnFocusTo?.closest('.product-gallery__slide')
                ? gallerySlides[activeProductIndex] : returnFocusTo;
              target?.focus({ preventScroll: true });
            },
          },
        );
        spotlightAnimations.open();

        const spotlight = document.querySelector('#spotlight');
        const controls = spotlight.querySelectorAll(
          '.spl-fullscreen, .spl-zoom-in, .spl-zoom-out, .spl-close, .spl-prev, .spl-next',
        );
        for (const control of controls) {
          control.tabIndex = 0;
          control.dataset.spotlightControl = '';
        }

        if (!spotlight.dataset.keyboardReady) {
          spotlight.dataset.keyboardReady = 'true';
          on(spotlight, 'keydown', (event) => {
            const control = event.target.closest('[data-spotlight-control]');
            if (control && (event.key === 'Enter' || event.key === ' ')) {
              event.preventDefault();
              control.click();
            }

            if (event.key !== 'Tab') return;
            const controls = [...spotlight.querySelectorAll('[data-spotlight-control]')].filter(
              (item) => item.getClientRects().length && getComputedStyle(item).visibility !== 'hidden',
            );
            if (!controls.length) return;
            const first = controls[0];
            const last = controls[controls.length - 1];
            if (event.shiftKey && document.activeElement === first) {
              event.preventDefault();
              last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
              event.preventDefault();
              first.focus();
            }
          });
        }

        root.inert = true;
        spotlight.querySelector('.spl-close')?.focus();
      };
      const normalizedQuantity = () => {
        const quantity = Number(quantityValue.value);
        return Number.isFinite(quantity) ? Math.max(1, Math.trunc(quantity)) : 1;
      };

      quantityButtons.forEach((button) =>
        on(button, 'click', () => {
          quantityValue.value = Math.max(1, normalizedQuantity() + Number(button.dataset.quantityChange));
        }),
      );

      on(quantityValue, 'change', () => {
        quantityValue.value = normalizedQuantity();
      });

      thumbnails.forEach((thumbnail, index) =>
        on(thumbnail, 'click', () => {
          setProductImage(index);
        }),
      );

      gallerySlides.forEach((slide) => on(slide, 'click', () => {
        if (swiper?.allowClick) openProductImage();
      }));
      on(productSection.querySelector('.product-gallery__zoom'), 'click', () => {
        if (gallerySlides.length) openProductImage();
      });
    }

    function initNotify() {
      const dialog = root.querySelector('[data-sheidlina-notify]');
      const form = dialog?.querySelector('form');
      const email = form?.elements.email;
      const submit = form?.querySelector('[type="submit"]');
      const error = form?.querySelector('[data-notify-error]');
      const offer = dialog?.querySelector('[data-notify-offer]');
      const success = dialog?.querySelector('[data-notify-success]');
      if (!dialog || !form || !email || !submit || !error || !offer || !success) return;

      const joined = new Set();
      let pending = false;
      let opener;
      let restoreScroll = () => {};

      function clearError() {
        error.hidden = true;
      }

      function close() {
        dialog.close();
      }

      function open(trigger) {
        if (dialog.open) return;
        opener = trigger;
        root.querySelector('.section--header.is-menu-open .header__menu')?.click();
        clearError();
        offer.hidden = false;
        success.hidden = true;
        restoreScroll = lockScroll();
        dialog.showModal();
      }

      function openFromClick(event) {
        const trigger = event.target.closest('[data-sheidlina-notify-open]');
        if (!trigger || trigger.tagName === 'FORM') return;
        event.preventDefault();
        open(trigger);
      }

      function openFromProductForm(event) {
        if (!event.target.matches('[data-sheidlina-notify-open]')) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        open(event.submitter || event.target.querySelector('[type="submit"]'));
      }

      function showSuccess() {
        offer.hidden = true;
        success.hidden = false;
        if (dialog.open) success.focus({ preventScroll: true });
      }

      function setSubmitting(value) {
        pending = value;
        email.readOnly = value;
        submit.disabled = value;
        submit.textContent = value ? form.dataset.sending : form.dataset.submitLabel;
      }

      async function submitEmail(event) {
        event.preventDefault();
        if (pending) return;
        clearError();
        email.value = email.value.trim();
        if (!email.checkValidity()) {
          error.textContent = form.dataset.invalidEmail;
          error.hidden = false;
          email.focus({ preventScroll: true });
          return;
        }

        const address = email.value.toLowerCase();
        if (joined.has(address)) {
          showSuccess();
          return;
        }

        setSubmitting(true);
        const request = new AbortController();
        const timeout = window.setTimeout(() => request.abort(), 30000);
        try {
          const response = await fetch(form.action, {
            method: 'POST',
            body: new FormData(form),
            signal: request.signal,
          });
          const result = await response.json().catch(() => null);
          if (!response.ok || result?.success !== true) throw new Error('Subscription failed');
          joined.add(address);
          window.igEvents = window.igEvents || [];
          window.igEvents.push({ event: 'form_submited' });
          showSuccess();
        } catch {
          error.textContent = form.dataset.error;
          error.hidden = false;
        } finally {
          window.clearTimeout(timeout);
          setSubmitting(false);
        }
      }

      on(root, 'click', openFromClick, true);
      on(root, 'submit', openFromProductForm, true);
      on(dialog.querySelector('[data-notify-close]'), 'click', close);
      on(dialog, 'click', (event) => {
        if (clickedOutsideDialog(event, dialog)) close();
      });
      on(dialog, 'cancel', (event) => {
        event.preventDefault();
        close();
      });
      on(dialog, 'close', () => {
        if (dialog.open) return;
        restoreScroll();
        if (opener?.isConnected) opener.focus({ preventScroll: true });
      });
      on(email, 'input', clearError);
      on(form, 'submit', submitEmail);
    }

    initNotify();
    if (gsap && ScrollTrigger && ScrollToPlugin && Spotlight) {
      gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);
      gsap.context(() => {
        initViewportResize();
        initHeader();
        initIntroVideo(initVideo());
        initChapters();
        initAboutMotion();
        initClosingPointer();
        initUi();
        initGallery(initSpotlight());
        initAnchors();
      }, root);

      const refresh = perFrame(() => ScrollTrigger.refresh(true));
      on(window, 'load', refresh, { once: true });
      root.querySelectorAll('img').forEach((image) => {
        let width = image.offsetWidth;
        let height = image.offsetHeight;
        on(image, 'load', () => {
          const nextWidth = image.offsetWidth;
          const nextHeight = image.offsetHeight;
          if (!phoneView.matches || width !== nextWidth || height !== nextHeight) refresh();
          width = nextWidth;
          height = nextHeight;
        });
      });
      document.fonts.ready.then(refresh);
      refresh();
    }

    instances.add(root);
  }

  document.querySelectorAll('[data-sheidlina-page]').forEach(initPage);
})();
